import React, { useMemo, useRef, useState, useEffect } from 'react';
import UploadFiles from '../UploadFiles'
import { useDispatch, useSelector } from 'react-redux';
import LineChartPreview from '../LineChartPreview'
import Modal from '../../reusables/Modal';
import { MdOutlineChevronRight } from "react-icons/md";
import { saveAllReportingPeaks, saveGamma, setQcFilesData, saveReportingPeaks, saveSigma, setStdFilesData, saveBuggingPeaks, saveAreaUnderPeaks, saveAreaUnderPeaksQc, saveAreaUnderPeaksUnk, saveStdXPeaks, saveQcXPeaks, saveUnkXPeaks, setUnkFilesData } from '../../reduxSlices/results/resultsSlice';
import { useNavigate } from 'react-router-dom';
import { peakClusteringAPI, peakProcessingAPI, reportingPeaksDetectorAPI, sigmaGammaEstimatorAPI, xRangeOptimizerAPI, peakUpdate2API, peakUpdate3API } from '../../api/api';
import Loader from '../shared/Loader';
import { logUserEvent } from '../../lib/utilities/logUserEvent';
import BackButton from '../shared/BackButton';
import store from '../../store';
import sanitizeKeysDeep from '../../lib/utilities/sanitizeKeyDeep';
import saveResultsToFirebase from '../../lib/utilities/saveResultsToFirebase';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useConfigNavigationGuard } from '../../hooks/useConfigNavigationGuard';
import { resetAll } from '../../reduxSlices/results/resultsSlice';



// Generate or retrieve from localStorage
const getOrCreateConfigId = () => {
  const existing = localStorage.getItem('currentConfigId');
  if (existing) return existing;

  const newId = uuidv4();
  localStorage.setItem('currentConfigId', newId);
  return newId;
};

const configId = getOrCreateConfigId();
function UploadUnkFiles() {
  const [files, setFiles] = useState([]);
  const [fileName, setFileName] = useState('');
  const [open, setOpen] = useState(false);
  const [chartPreviewData, setChartPreviewData] = useState([]);
  const dispatch = useDispatch();
  const maximumStandardName = useSelector(store => store.results.stdFilesPriority[store.results.stdFilesPriority.length - 1]);
  const minimumStandardName = useSelector(store => store.results.stdFilesPriority[0]);
  const unkFilesObj = useSelector(store => store.results.unkFiles);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const numStdFiles = useSelector(store => Object.keys(store.results.stdFiles).length);
  const numQcFiles = useSelector(store => Object.keys(store.results.qcFiles).length);
  const seededRef = useRef(false);

  // Navigation guard
  const { showExitModal, handleConfirmExit, handleCancelExit } = useConfigNavigationGuard(
    ['/configuration/peaksApproval', '/configuration/uploadQcFiles'],
    () => dispatch(resetAll())
  );

  const unkFilesList = useMemo(() => {
    if (!unkFilesObj) return [];
    return Object.keys(unkFilesObj).map(name => unkFilesObj[name]);
  }, [unkFilesObj]);

  useEffect(() => {
    if (!seededRef.current && unkFilesList.length > 0) {
      setFiles(unkFilesList);
      seededRef.current = true;
    }
  }, [unkFilesList]);



  const handleFileInput = (finalFiles) => {
    setFiles(finalFiles);
    // Store files locally, Redux action will happen when Next button is clicked
  }

  const getOriginalDataByName = (nameToFind) => {
    const source = files.length > 0 ? files : unkFilesList;
    const foundObject = source.find(item => (item.fileName || item.name) === nameToFind);
    return foundObject ? (foundObject.fileData?.originalData || foundObject.originalData) : [];
  };

  const handleFileName = (fileName) => {
    setOpen(true);
    setFileName(fileName)
    setChartPreviewData(getOriginalDataByName(fileName))
  }

  function findGlobalMinDifference(stdFilesWithXPeaksObj) {
    let globalMin = Infinity;

    Object.values(stdFilesWithXPeaksObj).forEach(file => {
      const x_peaks = file.x_peaks;
      if (!x_peaks || x_peaks.length < 2) return; // Need at least 2 to compare

      // Sort x_peaks just in case they're unordered
      const sortedPeaks = [...x_peaks].sort((a, b) => a - b);

      // Find minimum difference in this file
      let minDiff = Infinity;
      for (let i = 1; i < sortedPeaks.length; i++) {
        const diff = sortedPeaks[i] - sortedPeaks[i - 1];
        if (diff < minDiff) minDiff = diff;
      }
      // Update global minimum if this file's minDiff is smaller
      if (minDiff < globalMin) globalMin = minDiff;
    });

    return globalMin === Infinity ? null : globalMin;
  }

  // Helper function to convert API result format to Redux action format
  const convertToReduxFormat = (apiResult) => {
    return Object.entries(apiResult).map(([fileName, fileData]) => ({ fileName, fileData }));
  };

  const reportingPeaksDetector = async (stateAfterUpdate) => {
    try {
      const start = Date.now();
      const stdFilesWithXPeaksObj = {};
      const extractPeaks = (files) => {
        for (const key in files) {
          if (files.hasOwnProperty(key)) {
            // Handle both old and new file structure
            const fileData = files[key].fileData || files[key];
            stdFilesWithXPeaksObj[key] = { x_peaks: fileData.peaksXValues };
          }
        }
      };
      extractPeaks(stateAfterUpdate.stdFiles);
      extractPeaks(stateAfterUpdate.qcFiles);
      extractPeaks(stateAfterUpdate.unkFiles);

      // Find smallest difference between any two x_peaks
      const globalMinDifference = findGlobalMinDifference(stdFilesWithXPeaksObj);
      // console.log("Smallest difference between any two x_peaks:", globalMinDifference);

      //all x values of found peaks in std,qc and unk files
      const allXPeaks = Object.values(stdFilesWithXPeaksObj)
        .map(file => file.x_peaks) // Map to extract x_peaks arrays
        .flat(); // Flatten the array of arrays

      const maxStdXPeaks = stdFilesWithXPeaksObj[maximumStandardName]?.x_peaks ?? [];
      const minStdXPeaks = stdFilesWithXPeaksObj[minimumStandardName]?.x_peaks ?? [];
      const NumMinSamples = numStdFiles + numQcFiles;

      const maxStdReportingPeaks = await reportingPeaksDetectorAPI(stdFilesWithXPeaksObj, maximumStandardName, NumMinSamples, globalMinDifference * .99);

      const { bugging_peaks, allXPeaksResult } = await peakClusteringAPI(allXPeaks, maxStdXPeaks, minStdXPeaks, NumMinSamples, globalMinDifference * .99);

      const end = Date.now();
      logUserEvent("reporting_peaks_detected", {
        stdFileCount: Object.keys(stateAfterUpdate.stdFiles).length,
        qcFileCount: Object.keys(stateAfterUpdate.qcFiles).length,
        unkFileCount: Object.keys(stateAfterUpdate.unkFiles).length,
        totalXPeaks: allXPeaks.length,
        globalMinDiff: globalMinDifference,
        timeTakenMs: end - start,
        responseSummary: {
          buggingPeaksCount: bugging_peaks?.length ?? 0,
          allXPeaksResultCount: Object.keys(allXPeaksResult ?? {}).length
        }
      });

      dispatch(saveReportingPeaks(maxStdReportingPeaks));
      dispatch(saveAllReportingPeaks(allXPeaksResult));
      dispatch(saveBuggingPeaks(bugging_peaks));
    } catch (error) {
      console.error("Error in reportingPeaksDetector:", error);
      logUserEvent("reporting_peaks_detected_error", {
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  const xRangeOptimize = () => async (dispatch, getState) => {
    try {
      const state = getState();
      const { stdFiles, qcFiles, unkFiles } = state.results;

      const startStd = Date.now();
      const optimizedXStdFiles = await xRangeOptimizerAPI(stdFiles);
      const endStd = Date.now();
      logUserEvent("x_range_optimize_std", {
        fileCount: Object.keys(stdFiles).length,
        updatedCount: Object.keys(optimizedXStdFiles).length,
        timeTakenMs: endStd - startStd,
      });

      const startQc = Date.now();
      const optimizedXQcFiles = await xRangeOptimizerAPI(qcFiles);
      const endQc = Date.now();
      logUserEvent("x_range_optimize_qc", {
        fileCount: Object.keys(qcFiles).length,
        updatedCount: Object.keys(optimizedXQcFiles).length,
        timeTakenMs: endQc - startQc,
      });

      const startUnk = Date.now();
      const optimizedXUnkFiles = await xRangeOptimizerAPI(unkFiles);
      const endUnk = Date.now();
      logUserEvent("x_range_optimize_unk", {
        fileCount: Object.keys(unkFiles).length,
        updatedCount: Object.keys(optimizedXUnkFiles).length,
        timeTakenMs: endUnk - startUnk,
      });

      dispatch(setStdFilesData(convertToReduxFormat(optimizedXStdFiles)));
      dispatch(setQcFilesData(convertToReduxFormat(optimizedXQcFiles)));
      dispatch(setUnkFilesData(convertToReduxFormat(optimizedXUnkFiles)));

    } catch (error) {
      console.error("Failed to optimize X ranges:", error);
    }
  };

  const sigmaGammaEst = () => async (dispatch, getState) => {
    try {
      const start = Date.now();
      const state = getState();
      const { stdFiles, qcFiles, unkFiles } = state.results;

      const [stdResult, qcResult, unkResult] = await Promise.all([
        sigmaGammaEstimatorAPI(stdFiles),
        sigmaGammaEstimatorAPI(qcFiles),
        sigmaGammaEstimatorAPI(unkFiles),
      ]);

      // Merge all sigma values into a single array
      const allSigmaValues = [
        ...stdResult.sigmaList,
        ...qcResult.sigmaList,
        ...unkResult.sigmaList,
      ];

      // Merge all gamma values into a single array
      const allGammaValues = [
        ...stdResult.gammaList,
        ...qcResult.gammaList,
        ...unkResult.gammaList,
      ];

      const end = Date.now();
      logUserEvent("sigma_gamma_estimation", {
        stdSigmaValues: stdResult.sigmaList,
        qcSigmaValues: qcResult.sigmaList,
        unkSigmaValues: unkResult.sigmaList,
        stdGammaValues: stdResult.gammaList,
        qcGammaValues: qcResult.gammaList,
        unkGammaValues: unkResult.gammaList,
        allSigmaValues,
        allGammaValues,
        timeTakenMs: end - start,
      });

      dispatch(saveSigma(allSigmaValues));
      dispatch(saveGamma(allGammaValues));

    } catch (error) {
      console.error("Failed to estimate sigma and gamma:", error);
    }
  };

  const peakUpdate2 = () => async (dispatch, getState) => {
    try {
      const start = Date.now();
      const state = getState();
      const { stdFiles, qcFiles, unkFiles, allReportingPeaks } = state.results;

      const [stdResult, qcResult, unkResult] = await Promise.all([
        peakUpdate2API(stdFiles, allReportingPeaks),
        peakUpdate2API(qcFiles, allReportingPeaks),
        peakUpdate2API(unkFiles, allReportingPeaks),
      ]);

      const end = Date.now();
      logUserEvent("peak_update_2", {
        stdFileCount: Object.keys(stdFiles).length,
        qcFileCount: Object.keys(qcFiles).length,
        unkFileCount: Object.keys(unkFiles).length,
        updatedStdFilesCount: Object.keys(stdResult).length,
        updatedQcFilesCount: Object.keys(qcResult).length,
        updatedUnkFilesCount: Object.keys(unkResult).length,
        timeTakenMs: end - start
      });

      dispatch(setStdFilesData(convertToReduxFormat(stdResult)));
      dispatch(setQcFilesData(convertToReduxFormat(qcResult)));
      dispatch(setUnkFilesData(convertToReduxFormat(unkResult)));

    } catch (error) {
      console.error("Failed to peak update 2:", error);
    }
  };

  const peakUpdate3 = () => async (dispatch, getState) => {
    try {
      const start = Date.now();
      const state = getState();
      const { stdFiles, qcFiles, unkFiles, buggingPeaks } = state.results;

      const [stdResult, qcResult, unkResult] = await Promise.all([
        peakUpdate3API(stdFiles, buggingPeaks),
        peakUpdate3API(qcFiles, buggingPeaks),
        peakUpdate3API(unkFiles, buggingPeaks),
      ]);

      dispatch(setStdFilesData(convertToReduxFormat(stdResult)));
      dispatch(setQcFilesData(convertToReduxFormat(qcResult)));
      dispatch(setUnkFilesData(convertToReduxFormat(unkResult)));

      const end = Date.now();
      logUserEvent("peak_update_3", {
        stdFileCount: Object.keys(stdFiles).length,
        qcFileCount: Object.keys(qcFiles).length,
        unkFileCount: Object.keys(unkFiles).length,
        updatedStdFilesCount: Object.keys(stdResult).length,
        updatedQcFilesCount: Object.keys(qcResult).length,
        updatedUnkFilesCount: Object.keys(unkResult).length,
        buggingPeaksCount: buggingPeaks?.length ?? 0,
        timeTakenMs: end - start,
      });
    } catch (error) {
      console.error("Failed to peak update 2:", error);
    }
  };

  const peakProcessor = () => async (dispatch, getState) => {
    try {
      const start = Date.now();
      const state = getState();
      const { stdFiles, qcFiles, unkFiles } = state.results;
      const sigma_list = state.results.sigma;
      const gamma_list = state.results.gamma;

      const stdResult = await peakProcessingAPI(stdFiles, sigma_list, gamma_list);
      dispatch(setStdFilesData(convertToReduxFormat(stdResult)));
      const mid1 = Date.now();
      logUserEvent("peak_processing_std", {
        fileCount: Object.keys(stdFiles).length,
        timeTakenMs: mid1 - start,
        resultKeys: Object.keys(stdResult),
      });

      const qcResult = await peakProcessingAPI(qcFiles, sigma_list, gamma_list);
      dispatch(setQcFilesData(convertToReduxFormat(qcResult)));
      const mid2 = Date.now();
      logUserEvent("peak_processing_qc", {
        fileCount: Object.keys(qcFiles).length,
        timeTakenMs: mid2 - mid1,
        resultKeys: Object.keys(qcResult),
      });

      const unkResult = await peakProcessingAPI(unkFiles, sigma_list, gamma_list);
      const end = Date.now();
      logUserEvent("peak_processing_unk", {
        fileCount: Object.keys(unkFiles).length,
        timeTakenMs: end - mid2,
        resultKeys: Object.keys(unkResult),
      });

      dispatch(setUnkFilesData(convertToReduxFormat(unkResult)));
      // setProgress(100); 

    } catch (error) {
      console.error("Failed to process peaks:", error);
    }
  };

  // Helper function to calculate area under peak from y_comp and y_baseline data
  const calculateAreaFromPeakData = (peak) => {
    try {
      // For compositional peaks, use x_comp, y_comp, and y_baseline
      const x_vals = peak.x_comp || peak.x || [];
      const y_vals = peak.y_comp || peak.y || [];
      const baseline_vals = peak.y_baseline || [];

      if (!Array.isArray(x_vals) || !Array.isArray(y_vals) || !Array.isArray(baseline_vals)) {
        console.warn('Invalid peak data arrays for area calculation:', {
          x_comp: Array.isArray(peak.x_comp),
          y_comp: Array.isArray(peak.y_comp),
          y_baseline: Array.isArray(peak.y_baseline)
        });
        return 0;
      }

      if (x_vals.length !== y_vals.length || x_vals.length !== baseline_vals.length) {
        console.warn('Mismatched array lengths for area calculation:', {
          x_length: x_vals.length,
          y_length: y_vals.length,
          baseline_length: baseline_vals.length
        });
        return 0;
      }

      // Calculate area using trapezoidal integration
      let area = 0;
      for (let i = 1; i < x_vals.length; i++) {
        const x0 = x_vals[i - 1];
        const x1 = x_vals[i];
        const y0 = Math.max(0, (y_vals[i - 1] || 0) - (baseline_vals[i - 1] || 0));
        const y1 = Math.max(0, (y_vals[i] || 0) - (baseline_vals[i] || 0));
        const dx = x1 - x0;
        if (Number.isFinite(dx) && dx > 0) {
          area += 0.5 * (y0 + y1) * dx;
        }
      }
      return area;
    } catch (error) {
      console.error('Error calculating area from peak data:', error);
      return 0;
    }
  };

  const extractAreaUnderPeaks = (files, allReportingPeaks) => {
    // Output array to store the results
    const output = [];
    const output2 = [];

    try {
      // Loop through all keys in allReportingPeaks
      Object.keys(allReportingPeaks).forEach(reportingPeakKey => {
        const peakGroup = {}; // This will store the results for each allReportingPeaks key
        const peakGroup2 = {}; // This will store the results for each allReportingPeaks key (x_peak)

        // Go through each file
        Object.keys(files).forEach(fileKey => {
          const file = files[fileKey];
          // When files come from Redux, the data is already at the top level
          // When files come from component state, they might have the new fileData structure
          const fileData = file.fileData || file;
          const results = fileData.result;

          // Go through each result in the file
          Object.keys(results).forEach(resultKey => {
            const result = results[resultKey];

            // Check if 'to_analyze' is true
            if (result.to_analyze === true) {
              const peaks = result.peaks;

              // Go through each peak in the result
              Object.keys(peaks).forEach(peakKey => {
                const peakKeyFloat = parseFloat(peakKey); // Convert the peak key to a float
                const correspondingPeaks = allReportingPeaks[reportingPeakKey]; // Get the array of peaks

                // Check if the current peak key exists in the corresponding peaks
                if (correspondingPeaks.includes(peakKeyFloat)) {
                  // Calculate area from peak data instead of reading area_under_peak
                  // Note: Backend no longer provides area_under_peak in peak objects
                  const calculatedArea = calculateAreaFromPeakData(peaks[peakKey]);
                  peakGroup[fileKey] = calculatedArea;
                  peakGroup2[fileKey] = peaks[peakKey].x_peak;
                }
              });
            }
          });
        });

        // Add the group to the output
        output.push(peakGroup);
        output2.push(peakGroup2);
      });

      return { output, output2 };
    } catch (error) {
      console.error('extractAreaUnderPeaks: Error processing peaks', error);
      throw error;
    }
  }


  // Helper function to compare file arrays
  const areFilesEqual = (currentFiles, reduxFiles) => {
    if (currentFiles.length !== reduxFiles.length) return false;

    // Compare file names and basic properties
    for (let i = 0; i < currentFiles.length; i++) {
      if (currentFiles[i].name !== (reduxFiles[i].fileName || reduxFiles[i].name)) return false;
      // Could add more detailed comparison if needed
    }

    return true;
  };

  const handleNextScreen = async () => {
    const effectiveFiles = files.length > 0 ? files : unkFilesList;

    // Check if files have actually changed by comparing with Redux state
    const hasChanges = !areFilesEqual(effectiveFiles, unkFilesList);

    // If no changes, just navigate without processing
    if (!hasChanges && Object.keys(unkFilesObj).length > 0) {
      navigate('/configuration/peaksApproval');
      return;
    }

    setLoading(true);

    try {
      await dispatch(async (dispatch, getState) => {
        await dispatch(setUnkFilesData(effectiveFiles));
        logUserEvent("upload_unk_files", {
          fileCount: effectiveFiles.length,
          fileNames: effectiveFiles.map(f => f.fileName || f.name),
        });
        const stateAfterUpdate = getState().results;
        setProgress(25);
        setLoadingText('Reading Files');
        await reportingPeaksDetector(stateAfterUpdate);
        setProgress(40);
        setLoadingText('Detecting Peaks');
      });

      setProgress(50);
      setLoadingText('Estimating Sigma and Gamma');
      await dispatch(sigmaGammaEst());
      setProgress(60);
      setLoadingText('Working on Detected Peaks');
      await dispatch(peakUpdate2());
      setProgress(70);
      setLoadingText(`Working on Bugging Peaks`);
      await dispatch(peakUpdate3());
      setProgress(80);
      setLoadingText('Optimizing X Ranges');
      await dispatch(xRangeOptimize());
      setProgress(90);
      setLoadingText('Processing Peaks');
      await dispatch(peakProcessor());
      setProgress(100);

      // The API results have already been converted and stored via the earlier API calls

      // Then process area under peaks
      await dispatch(async (dispatch, getState) => {
        const start = Date.now();

        const state = getState();
        const stdFiles = state.results.stdFiles;
        const qcFiles = state.results.qcFiles;
        const unkFiles = state.results.unkFiles;
        const allReportingPeaks = state.results.allReportingPeaks;
        try {
          // Add area under peaks calculations
          const { output: stdAreaUnderPeaks, output2: stdXPeaks } = extractAreaUnderPeaks(stdFiles, allReportingPeaks);
          await dispatch(saveAreaUnderPeaks(stdAreaUnderPeaks));
          await dispatch(saveStdXPeaks(stdXPeaks));

          const { output: qcAreaUnderPeaks, output2: qcXPeaks } = extractAreaUnderPeaks(qcFiles, allReportingPeaks);
          await dispatch(saveAreaUnderPeaksQc(qcAreaUnderPeaks));
          await dispatch(saveQcXPeaks(qcXPeaks));

          const { output: unkAreaUnderPeaks, output2: unkXPeaks } = extractAreaUnderPeaks(unkFiles, allReportingPeaks);
          await dispatch(saveAreaUnderPeaksUnk(unkAreaUnderPeaks));
          await dispatch(saveUnkXPeaks(unkXPeaks));
        } catch (error) {
          console.error('Error in area under peaks processing:', error);
          throw error;
        }

        const end = Date.now();
        logUserEvent("area_under_peak_extracted", {
          stdFileCount: Object.keys(stdFiles).length,
          qcFileCount: Object.keys(qcFiles).length,
          unkFileCount: Object.keys(unkFiles).length,
          reportingPeaksCount: Object.keys(allReportingPeaks).length,
          timeTakenMs: end - start
        });
      });


      try {
        const finalResults = store.getState().results;
        const writes = [];

        // Save stdFilesPriority (deferred from UploadStdFiles) - Removed Firebase call
        // if (finalResults.stdFilesPriority && finalResults.stdFilesPriority.length) {
        //   writes.push(saveResultsToFirebase(configId, {
        //     stdFilesPriority: finalResults.stdFilesPriority.map(name => name.replace(/[.#$/[\]]/g, '_'))
        //   }));
        // }

        // Save areaUnderPeaks and related X peaks (std/qc/unk) in one payload if present
        const areaPayload = {};
        if (finalResults.areaUnderPeaks && finalResults.areaUnderPeaks.length) {
          areaPayload.areaUnderPeaks = sanitizeKeysDeep(finalResults.areaUnderPeaks);
        }
        if (finalResults.areaUnderPeaksQc && finalResults.areaUnderPeaksQc.length) {
          areaPayload.areaUnderPeaksQc = sanitizeKeysDeep(finalResults.areaUnderPeaksQc);
        }
        if (finalResults.areaUnderPeaksUnk && finalResults.areaUnderPeaksUnk.length) {
          areaPayload.areaUnderPeaksUnk = sanitizeKeysDeep(finalResults.areaUnderPeaksUnk);
        }
        if (finalResults.stdXPeaks && finalResults.stdXPeaks.length) {
          areaPayload.stdXPeaks = sanitizeKeysDeep(finalResults.stdXPeaks);
        }
        if (finalResults.qcXPeaks && finalResults.qcXPeaks.length) {
          areaPayload.qcXPeaks = sanitizeKeysDeep(finalResults.qcXPeaks);
        }
        if (finalResults.unkXPeaks && finalResults.unkXPeaks.length) {
          areaPayload.unkXPeaks = sanitizeKeysDeep(finalResults.unkXPeaks);
        }
        // if (Object.keys(areaPayload).length) writes.push(saveResultsToFirebase(configId, areaPayload)); // Removed Firebase call

        // Sigma / Gamma
        const sigmaGamma = {};
        if (finalResults.sigma && finalResults.sigma.length) sigmaGamma.sigma = sanitizeKeysDeep(finalResults.sigma);
        if (finalResults.gamma && finalResults.gamma.length) sigmaGamma.gamma = sanitizeKeysDeep(finalResults.gamma);
        // if (Object.keys(sigmaGamma).length) writes.push(saveResultsToFirebase(configId, sigmaGamma)); // Removed Firebase call

        // Reporting related (potentially large but still lightweight compared to file blobs)
        const reporting = {};
        if (finalResults.reportingPeaks && Object.keys(finalResults.reportingPeaks).length) reporting.reportingPeaks = sanitizeKeysDeep(finalResults.reportingPeaks);
        if (finalResults.buggingPeaks && Object.keys(finalResults.buggingPeaks).length) reporting.buggingPeaks = sanitizeKeysDeep(finalResults.buggingPeaks);
        if (finalResults.allReportingPeaks && Object.keys(finalResults.allReportingPeaks).length) reporting.allReportingPeaks = sanitizeKeysDeep(finalResults.allReportingPeaks);
        // if (Object.keys(reporting).length) writes.push(saveResultsToFirebase(configId, reporting)); // Removed Firebase call

        // Execute writes sequentially
        for (const write of writes) {
          await write;
        }

      } catch (error) {
        console.error("Error saving unknown files + analysis results to Firebase:", error);
      }

      navigate('/configuration/peaksApproval');

    } catch (error) {
      console.error("Error in handleNextScreen:", error);
    } finally {
      setLoading(false);
    }


  };




  return (
    <>
      <ConfirmationModal
        isOpen={showExitModal}
        onClose={handleCancelExit}
        onConfirm={handleConfirmExit}
        title="Leave Configuration?"
        message="If you leave now, the current configuration will not be saved and all progress will be lost. Are you sure you want to continue?"
        confirmText="Leave"
        cancelText="Stay"
        type="warning"
      />
      {loading ? <Loader progress={progress} text={loadingText} /> : (
        <div className='flex flex-col px-4 items-center justify-center mt-6'>
          <h2 className='text-lg  mb-8 self-start'>Upload your <span className='font-bold  text-customeOrange'>Unknown</span> files by drag and drop or click in the box below.</h2>
        </div>
      )}
      <UploadFiles
        moreColors={true}
        showHelpText={false}
        handleFileInput={handleFileInput}
        fileType={'unknown'}
        handleFileName={handleFileName}
        level={false}
        initialFiles={files.length > 0 ? files : unkFilesList}
        enableReorder={false}
      />
      <div className='fixed bottom-8 right-8 z-40 flex gap-2'>
        <BackButton to={'/configuration/uploadQcFiles'} />
        {/* <div className='flex items-center justify-center'>
                  <IoMdArrowBack />
                  <button 
                      // onClick={onBack} 
                      className='text-lg text-indigo-700 ml-2'>Prev</button>
              </div> */}
        <button
          disabled={(files.length === 0 && unkFilesList.length === 0)}
          onClick={() => handleNextScreen()}
          className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
        >Next
          <MdOutlineChevronRight className='ml-6' fontSize={24} />
        </button>
      </div>

      <Modal open={open} onClose={() => setOpen(false)}>
        <LineChartPreview data={chartPreviewData} fileName={fileName} />
      </Modal>
    </>

  )
}

export default UploadUnkFiles

// {stdFiles:{a:{name:'', type:'standard', peaksXValues:[], ...}, b:{name:'', type:'standard', peaksXValues:[], ...}, ... }, qcFiles:{c:{name:'', type:'qualityControl', peaksXValues:[], ...}, d:{name:'', type:'qualityControl', peaksXValues:[], ...}, ... }, unkFiles:{e:{name:'', type:'unknown', peaksXValues:[], ...}, f:{name:'', type:'unknown', peaksXValues:[], ...}, ... }, otherFiles:{}, ...}
// {a:{x_peaks: peaksXValues}, b:{x_peaks: peaksXValues}, c:{x_peaks: peaksXValues}, d:{x_peaks: peaksXValues}, e:{x_peaks: peaksXValues}, f:{x_peaks: peaksXValues}, ... }
// files: {
//   '1.txt' : {
//     ...,
//     result : {
//       2:{
//         'to_analyze' : '',
//         peaks: {
//           3.76 : {
//             'area_under_peak' : 12
//           },
//           4.96 : {
//             'area_under_peak' : 1
//           },
//           ...
//         }
//       },
//       ...
//     }
//   },
//   '2.txt' : {
//     ...,
//     result : {
//       8:{
//         'to_analyze' : '',
//         peaks: {
//           2.74 : {
//             'area_under_peak' : 15
//           },
//           5.96 : {
//             'area_under_peak' : 10
//           },
//           ...
//         }
//       },
//       ...
//     }
//   },
//   ...
// }
// allReportingPeaks: {
//   3.743544 : [3.65 3.09, ...],
//   4.364544 : [4.61 4.69, ...],
//   ...
// }